import { createAction, createReducer } from '@reduxjs/toolkit';
import { USER } from 'reactApp/appHelpers/configHelpers';
import type { ApiProfile, Profile, ProfileOverquota } from 'reactApp/modules/profile/profile.types';

const initialState: Profile = {
    email: '',
    checkUpdateLicenseCompleted: false,
    ctime: 0,
    overquota: {
        state: USER?.cloud?.profile?.overquota?.state,
        blockTime: (USER?.cloud?.profile?.overquota?.block_time || 0) * 1000,
        clearTime: (USER?.cloud?.profile?.overquota?.clear_time || 0) * 1000,
        status: null,
    },
    version: '',
    devices: {},
    isLoaded: false,
    isLoading: false,
    flags: [],
};

export const loadProfileRequest = createAction('@profile/clearError');
export const loadProfileSuccess = createAction<{ profile: ApiProfile }>('@profile/loadProfileSuccess');
export const loadProfileFailure = createAction('@profile/loadProfileFailure');
export const checkUpdateLicenseRequest = createAction('@profile/user-la/request');
export const agreeUpdateLicenseRequest = createAction<{ source: string; isUpdate?: boolean }>('@profile/agree-la/request');
export const checkUpdateLicenseCompleted = createAction('profile/checkUpdateLicenseCompleted');
export const setProfileLoadStateStart = createAction('profile/setProfileLoadStateStart');
export const setProfileLoadStateFailure = createAction('profile/setProfileLoadStateFailure');
export const setProfileLoadStateSuccess = createAction<
    ProfileOverquota & { version?: string; devices?: Profile['devices']; ctime?: number; flags?: Profile['flags'] }
>('profile/setProfileLoadStateSuccess');
export const setProfileUblockUser = createAction('profile/setProfileUblockUser');
export const setClearedUserProcessed = createAction<{ forceLA?: boolean }>('profile/setClearedUserProcessed');
export const requestDataRestore = createAction('profile/requestDataRestore');
export const resetProfileOverquotaState = createAction('profile/resetProfileOverquotaState');

export const profileReducer = createReducer(initialState, {
    [loadProfileSuccess.type]: (state, action: ReturnType<typeof loadProfileSuccess>): void => {
        const { profile } = action.payload;

        const { properties, email } = profile;

        state.email = email;
        state.overquota = {
            clearTime: (properties['overquota.clear_time'] || 0) * 1000,
            clearStarted: (properties['overquota.clear_started'] || 0) * 1000,
            startPipeline: (properties['overquota.start_pipeline_time'] || 0) * 1000,
            status: 'loaded',
        };
    },
    [checkUpdateLicenseCompleted.type]: (state): void => {
        state.checkUpdateLicenseCompleted = true;
    },
    [resetProfileOverquotaState.type]: (state): void => {
        state.overquota = {
            state: undefined,
        };
    },
    [setProfileLoadStateStart.type]: (state) => {
        state.isLoading = true;
    },
    [setProfileLoadStateFailure.type]: (state) => {
        state.isLoading = false;

        state.overquota = {
            status: 'failed',
        };
    },
    [setProfileLoadStateSuccess.type]: (state, action: ReturnType<typeof setProfileLoadStateSuccess>) => {
        state.isLoading = false;
        state.isLoaded = true;

        state.overquota = {
            ...state.overquota,
            ...action.payload,
        };
        if (action.payload.version) {
            state.version = action.payload.version;
        }

        if (action.payload.state) {
            state.overquota.status = 'loaded';
        }

        if (action.payload.devices) {
            state.devices = {
                ...state.devices,
                ...action.payload.devices,
            };
        }

        if (action.payload.flags) {
            state.flags = [...state.flags, ...action.payload.flags];
        }

        if (action.payload.ctime) {
            state.ctime = action.payload.ctime * 1000;
        }

        if (action.payload.cycleCount) {
            state.cycleCount = action.payload.cycleCount;
        }
    },
});
